import React, { useEffect, useState } from 'react';
import { bool, func, object, string } from 'prop-types';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import Swal from 'sweetalert2';

import { FormattedMessage, intlShape, injectIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { sendTeamAmMessage } from '../../../util/api';
import {
  Modal,
  Form,
  FieldTextInput,
  FieldPhoneNumberInput,
  PrimaryButton,
  ExternalLink,
  IconMail,
} from '../../../components';
import TeamAmPortrait from '../TeamAmPortrait/TeamAmPortrait';

import css from './TeamAmContactModal.module.css';

const MESSAGE_TAB = 'message';
const CALL_TAB = 'call';

/**
 * Turns the old two-tab dialog back on.
 *
 * The dialog used to open on two tabs: "Send a Message", an enquiry form posted
 * to the manager by email, and "Request a Call", a link to their booking
 * calendar. Both are now replaced by the direct WhatsApp, phone and email links
 * below, because a member who already has a manager wants to reach them, not to
 * fill in a form about themselves.
 *
 * The tab code is kept, not deleted, because we may want the form back. Set this
 * to true and the dialog goes back to exactly what it was; nothing else has to
 * change, and the server route behind the form (sendTeamAmMessage) is untouched.
 */
const SHOW_CONTACT_TABS = false;

/** The brand glyph, so the WhatsApp row is recognisable at a glance. */
const WhatsAppIcon = () => (
  <svg
    className={classNames(css.rowIcon, css.rowIconWhatsApp)}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
  </svg>
);

const PhoneIcon = () => (
  <svg
    className={css.rowIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92" />
  </svg>
);

/* The envelope moved to components/IconMail when the account manager cards
   needed the same glyph on their button. The size and colour still come from
   css.rowIcon here, so the row is unchanged. */
const MailIcon = () => <IconMail rootClassName={css.rowIcon} />;

/** The "opens in a new tab" marker on the WhatsApp row. */
const ExternalIcon = () => (
  <svg
    className={css.rowExternal}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M13 4h7v7" />
    <path d="M20 4 10 14" />
    <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
  </svg>
);

/**
 * The contact dialog for one account manager.
 *
 * It lists the ways to reach them directly: a WhatsApp chat, their phone number
 * and their email address. Each row appears only if that detail came down with
 * the manager, so the dialog never shows an empty line.
 *
 * There is no flag here for hiding the number from a passing visitor. The
 * server decides that: it leaves the phone out of the response entirely unless
 * the reader is signed in, so on the public Team page there is no number to
 * hide. That is a real change — the numbers used to be in the JavaScript
 * bundle, where hiding the row hid nothing from anybody who opened it.
 *
 * The line above the name is the `eyebrow` prop, and falls back to the member's
 * market. See the note beside it for why the two pages differ.
 *
 * `mailtoSubject` pre-fills the subject of the email row. The account manager
 * cards on the search and listing pages pass one, so a manager can see which
 * desk or which listing a mail is about. The Team and Subscriptions pages pass
 * nothing and get a plain mailto, as before.
 *
 * Members with a `contactUrl` (Jack Collins) never reach this component. The
 * Team page grid and bio, and the Subscriptions page card, all send them
 * straight to that page instead.
 *
 * Set SHOW_CONTACT_TABS above to bring back the old enquiry form and booking
 * calendar tabs.
 */
const TeamAmContactModal = props => {
  const {
    isOpen,
    member,
    eyebrow,
    mailtoSubject,
    whatsappText,
    onClose,
    onManageDisableScrolling,
    intl,
  } = props;

  const [activeTab, setActiveTab] = useState(MESSAGE_TAB);
  const [submitting, setSubmitting] = useState(false);

  // Every fresh open starts on "Send a Message", including when the dialog is
  // reopened for a different account manager.
  const memberSlug = member ? member.slug : null;
  useEffect(() => {
    if (isOpen) {
      setActiveTab(MESSAGE_TAB);
    }
  }, [isOpen, memberSlug]);

  if (!member) {
    return null;
  }

  const onSubmit = (values, form) => {
    setSubmitting(true);

    // Only the account manager's slug goes to the server. The server holds the
    // email addresses, so a caller cannot point this form at any address.
    return sendTeamAmMessage({
      accountManager: member.slug,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      countryCode: values.countryCode,
      phoneNumber: values.phoneNumber,
      linkedinUrl: values.linkedinUrl,
      company: values.company,
      subject: values.subject,
      message: values.message,
    })
      .then(() => {
        Swal.fire({
          title: intl.formatMessage({ id: 'TAM.alert.success.title' }),
          text: intl.formatMessage(
            { id: 'TAM.alert.success.text' },
            { firstName: member.firstName }
          ),
          icon: 'success',
          showCloseButton: true,
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
        });
        form.restart();
        onClose();
      })
      .catch(() => {
        Swal.fire({
          title: intl.formatMessage({ id: 'TAM.alert.fail.title' }),
          text: intl.formatMessage({ id: 'TAM.alert.fail.text' }),
          icon: 'error',
          showCloseButton: true,
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const onMessageTab = activeTab === MESSAGE_TAB;

  // The email row's link. The account manager cards pass a subject so the
  // manager can see at a glance which desk or which listing the mail is about
  // — "Parnexus — Collectable Cars enquiry", "Parnexus — Listing #87376".
  //
  // WHY THE "#" LOOKS LIKE "%23" IN THE HREF
  //   It has to. A raw "#" starts the fragment of a URI, so leaving it alone
  //   would cut the subject off right before the number — the mail client would
  //   see "Parnexus — Listing " and drop the rest. Percent-encoding it to %23 is
  //   the only way to carry the character through, and the mail client decodes
  //   it back to "#" when it fills in the subject line.
  //
  //   So "%23" in the link is correct and expected. If a "%23" ever shows up in
  //   the composed SUBJECT LINE itself, that is the mail client failing to
  //   decode, and nothing on this side can fix it — the only workaround would be
  //   to drop the "#" from the copy.
  //
  // encodeURIComponent, not encodeURI: encodeURI leaves "#" alone, which is
  // exactly the truncation described above.
  const mailtoHref = member.email
    ? `mailto:${member.email}${
        mailtoSubject ? `?subject=${encodeURIComponent(mailtoSubject)}` : ''
      }`
    : null;

  // The same idea for WhatsApp: the cards pass an opening line so the manager
  // sees which listing or which desk the chat is about.
  //
  // The "#" in the listing reference is percent-encoded here too, for the same
  // reason as above. This side is not in doubt though: wa.me is an ordinary
  // https URL, so the browser and WhatsApp both decode it properly and the
  // message opens with a real "#".
  //
  // wa.me carries it in ?text=. The separator is worked out rather than assumed,
  // because whatsappUrlOf builds the base URL on the server and a query string
  // could be added to it there later without this file being touched.
  const whatsappHref = member.whatsappUrl
    ? `${member.whatsappUrl}${
        whatsappText
          ? `${member.whatsappUrl.includes('?') ? '&' : '?'}text=${encodeURIComponent(
              whatsappText
            )}`
          : ''
      }`
    : null;

  // The old enquiry form and booking calendar. Kept for the day we want them
  // back; only rendered while SHOW_CONTACT_TABS is true.
  const tabsContent = (
    <>
      <button className={css.close} type="button" onClick={onClose}>
        <FormattedMessage id="TAM.dialog.close" />
      </button>

      <p className={css.vertical}>{member.vertical}</p>
      <h2 className={css.heading}>
        <FormattedMessage id="TAM.dialog.title" values={{ name: member.name }} />
      </h2>

      <div className={css.tabs} role="tablist">
        <button
          className={classNames(css.tab, { [css.tabActive]: onMessageTab })}
          type="button"
          role="tab"
          aria-selected={onMessageTab}
          onClick={() => setActiveTab(MESSAGE_TAB)}
        >
          <FormattedMessage id="TAM.dialog.messageTab" />
        </button>
        <button
          className={classNames(css.tab, { [css.tabActive]: !onMessageTab })}
          type="button"
          role="tab"
          aria-selected={!onMessageTab}
          onClick={() => setActiveTab(CALL_TAB)}
        >
          <FormattedMessage id="TAM.dialog.callTab" />
        </button>
      </div>

      {onMessageTab ? (
        <FinalForm
          onSubmit={onSubmit}
          render={fieldRenderProps => {
            const { handleSubmit, invalid } = fieldRenderProps;

            return (
              <Form className={css.form} onSubmit={handleSubmit}>
                <div className={css.nameRow}>
                  <FieldTextInput
                    className={css.field}
                    type="text"
                    id="TeamAmContactModal.firstName"
                    name="firstName"
                    autoComplete="given-name"
                    label={intl.formatMessage({ id: 'TAM.dialog.firstNameLabel' })}
                    placeholder={intl.formatMessage({ id: 'TAM.dialog.firstNamePlaceholder' })}
                    validate={validators.required(
                      intl.formatMessage({ id: 'TAM.dialog.firstNameRequired' })
                    )}
                  />

                  <FieldTextInput
                    className={css.field}
                    type="text"
                    id="TeamAmContactModal.lastName"
                    name="lastName"
                    autoComplete="family-name"
                    label={intl.formatMessage({ id: 'TAM.dialog.lastNameLabel' })}
                    placeholder={intl.formatMessage({ id: 'TAM.dialog.lastNamePlaceholder' })}
                    validate={validators.required(
                      intl.formatMessage({ id: 'TAM.dialog.lastNameRequired' })
                    )}
                  />
                </div>

                <FieldTextInput
                  className={css.field}
                  type="email"
                  id="TeamAmContactModal.email"
                  name="email"
                  autoComplete="email"
                  label={intl.formatMessage({ id: 'TAM.dialog.emailLabel' })}
                  placeholder={intl.formatMessage({ id: 'TAM.dialog.emailPlaceholder' })}
                  validate={validators.composeValidators(
                    validators.required(intl.formatMessage({ id: 'TAM.dialog.emailRequired' })),
                    validators.emailFormatValid(
                      intl.formatMessage({ id: 'TAM.dialog.emailInvalid' })
                    )
                  )}
                />

                {/* No css.field here: the component's own wrapper carries the
                    row spacing, and a margin on the input would push it out of
                    line with the country-code select beside it. */}
                <FieldPhoneNumberInput
                  id="TeamAmContactModal.phoneNumber"
                  name="phoneNumber"
                  countryCodeName="countryCode"
                  label={intl.formatMessage({ id: 'TAM.dialog.phoneLabel' })}
                  placeholder={intl.formatMessage({ id: 'TAM.dialog.phonePlaceholder' })}
                  validate={validators.required(
                    intl.formatMessage({ id: 'TAM.dialog.phoneRequired' })
                  )}
                />

                <FieldTextInput
                  className={css.field}
                  type="text"
                  id="TeamAmContactModal.linkedinUrl"
                  name="linkedinUrl"
                  label={intl.formatMessage({ id: 'TAM.dialog.linkedinLabel' })}
                  placeholder={intl.formatMessage({ id: 'TAM.dialog.linkedinPlaceholder' })}
                />

                <FieldTextInput
                  className={css.field}
                  type="text"
                  id="TeamAmContactModal.company"
                  name="company"
                  autoComplete="organization"
                  label={intl.formatMessage({ id: 'TAM.dialog.companyLabel' })}
                  placeholder={intl.formatMessage({ id: 'TAM.dialog.companyPlaceholder' })}
                  validate={validators.required(
                    intl.formatMessage({ id: 'TAM.dialog.companyRequired' })
                  )}
                />

                <FieldTextInput
                  className={css.field}
                  type="text"
                  id="TeamAmContactModal.subject"
                  name="subject"
                  label={intl.formatMessage({ id: 'TAM.dialog.subjectLabel' })}
                  placeholder={intl.formatMessage({ id: 'TAM.dialog.subjectPlaceholder' })}
                  validate={validators.required(
                    intl.formatMessage({ id: 'TAM.dialog.subjectRequired' })
                  )}
                />

                <FieldTextInput
                  className={css.field}
                  type="textarea"
                  id="TeamAmContactModal.message"
                  name="message"
                  label={intl.formatMessage({ id: 'TAM.dialog.messageLabel' })}
                  placeholder={intl.formatMessage({ id: 'TAM.dialog.messagePlaceholder' })}
                  validate={validators.required(
                    intl.formatMessage({ id: 'TAM.dialog.messageRequired' })
                  )}
                />

                <PrimaryButton
                  className={css.submitButton}
                  type="submit"
                  inProgress={submitting}
                  disabled={invalid || submitting}
                >
                  <FormattedMessage id="TAM.dialog.submit" />
                </PrimaryButton>

                <p className={css.note}>
                  <FormattedMessage
                    id="TAM.dialog.confidentialNote"
                    values={{ firstName: member.firstName }}
                  />
                </p>
              </Form>
            );
          }}
        />
      ) : (
        <div className={css.callTab}>
          <p className={css.callText}>
            <FormattedMessage
              id="TAM.dialog.callText"
              values={{ firstName: member.firstName }}
            />
          </p>
          <ExternalLink className={css.calendarButton} href={member.calendarUrl}>
            <FormattedMessage id="TAM.dialog.openCalendar" />
            <i className={css.calendarLine} aria-hidden="true" />
          </ExternalLink>
        </div>
      )}
    </>
  );

  // The direct ways to reach the manager. The WhatsApp and phone rows drop out
  // when no number came down — either because we have none for that manager,
  // or because the reader is not signed in and the server withheld it. The
  // email row is always there, so the list is never empty.
  const contactContent = (
    <div>
      <div className={css.contactHeader}>
        {/* The round frame lives on the wrapper, not on TeamAmPortrait itself,
            whose own 4:5 rounded rectangle is simply clipped away by it.

            The SQUARE crop is used here, falling back to the tall portrait for
            a manager who has no square. A square source fills this square box
            exactly, so the head stays centred in the circle. The portrait had
            to be cropped to fit, and its framing is tuned for the tall grid
            tile rather than for a 64px circle. */}
        <span className={css.contactAvatar}>
          <TeamAmPortrait
            className={css.contactPortrait}
            photo={member.squarePhoto || member.photo}
            name={member.name}
            initials={member.initials}
          />
        </span>
        <div className={css.contactHeaderText}>
          {/* The Subscriptions page passes "Your account manager", because
              there the member is looking at their own manager. The Team page
              passes nothing: those are eight different people, only one of whom
              may be theirs, so each one is named by their market instead. */}
          <p className={css.contactEyebrow}>{eyebrow || member.vertical}</p>
          <h2 className={css.contactName}>{member.name}</h2>
        </div>
      </div>

      <div className={css.rows}>
        {whatsappHref ? (
          <ExternalLink className={css.row} href={whatsappHref}>
            <WhatsAppIcon />
            <span className={css.rowText}>
              <span className={css.rowTitle}>
                <FormattedMessage id="TAM.dialog.whatsAppTitle" />
              </span>
              <span className={css.rowSubtitle}>
                <FormattedMessage id="TAM.dialog.whatsAppSubtitle" />
              </span>
            </span>
            <ExternalIcon />
          </ExternalLink>
        ) : null}

        {member.phone ? (
          <a
            className={css.row}
            href={`tel:${member.phone}`}
            aria-label={intl.formatMessage(
              { id: 'TAM.dialog.callLabel' },
              { firstName: member.firstName }
            )}
          >
            <PhoneIcon />
            <span className={css.rowText}>
              <span className={css.rowTitle}>{member.phone}</span>
            </span>
          </a>
        ) : null}

        {member.email ? (
          <a
            className={css.row}
            href={mailtoHref}
            aria-label={intl.formatMessage(
              { id: 'TAM.dialog.emailLabelAria' },
              { firstName: member.firstName }
            )}
          >
            <MailIcon />
            <span className={css.rowText}>
              <span className={css.rowTitle}>{member.email}</span>
            </span>
          </a>
        ) : null}
      </div>

      <button className={css.contactClose} type="button" onClick={onClose}>
        <FormattedMessage id="TAM.dialog.close" />
      </button>
    </div>
  );

  return (
    <Modal
      id="TeamAmContactModal"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      hideCloseBtn
      closeOnBackdropClick
      containerClassName={css.container}
      contentClassName={SHOW_CONTACT_TABS ? css.content : css.contactContent}
    >
      {SHOW_CONTACT_TABS ? tabsContent : contactContent}
    </Modal>
  );
};

TeamAmContactModal.defaultProps = {
  member: null,
  eyebrow: null,
  mailtoSubject: null,
  whatsappText: null,
};

TeamAmContactModal.propTypes = {
  isOpen: bool.isRequired,
  member: object,
  /**
   * The small line above the name. Defaults to the member's market, which is
   * what the Team page wants. Pass "Your account manager" where the dialog is
   * only ever opened on the viewer's own manager.
   */
  eyebrow: string,
  /**
   * Pre-fills the subject of the email link. The account manager cards pass one
   * so the manager can see which desk or which listing a mail is about. Left
   * out on the Team and Subscriptions pages, where a plain mailto is right.
   */
  mailtoSubject: string,
  /**
   * Pre-fills the WhatsApp message. Same purpose as mailtoSubject: the account
   * manager cards pass one, the Team and Subscriptions pages pass nothing and
   * open an empty chat, as before.
   */
  whatsappText: string,
  onClose: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(TeamAmContactModal);
